type BeforeInstallPromptOutcome = "accepted" | "dismissed";
export type AppDownloadResult = "installed" | "dismissed" | "guided" | "unsupported";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: BeforeInstallPromptOutcome;
    platform: string;
  }>;
}

const LAUNCH_CHOICE_STORAGE_KEY = "ironfleet.crm.launch-choice.v1";

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
let installPromptListenerAttached = false;

function isStandalone(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const mediaQuery = window.matchMedia?.("(display-mode: standalone)");
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return Boolean(mediaQuery?.matches || iosStandalone);
}

function isIosBrowser(): boolean {
  const userAgent = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent);
  return isIos && isSafari;
}

function isAndroidOrIosBrowser(): boolean {
  const userAgent = window.navigator.userAgent;
  return /Android|iPad|iPhone|iPod/.test(userAgent);
}

function canUseStorage(): boolean {
  try {
    const probe = "__launch_choice_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export function canOfferAppDownload(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  if (isStandalone()) {
    return false;
  }
  return isAndroidOrIosBrowser();
}

export function captureInstallPrompt(): void {
  if (typeof window === "undefined" || installPromptListenerAttached) {
    return;
  }
  installPromptListenerAttached = true;
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
  });
}

function readLaunchChoiceSeen(): boolean {
  if (!canUseStorage()) {
    return false;
  }
  return window.localStorage.getItem(LAUNCH_CHOICE_STORAGE_KEY) === "seen";
}

function writeLaunchChoiceSeen(): void {
  if (!canUseStorage()) {
    return;
  }
  window.localStorage.setItem(LAUNCH_CHOICE_STORAGE_KEY, "seen");
}

type LaunchChoice = "browser" | "install";

function presentLaunchChoiceDialog(): Promise<LaunchChoice> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "launch-choice-overlay";
    overlay.innerHTML = `
      <div class="launch-choice-card" role="dialog" aria-modal="true" aria-labelledby="launch-choice-title">
        <h2 id="launch-choice-title">Open Ironfleet CRM</h2>
        <p>Choose how you want to launch the CRM on this device.</p>
        <div class="launch-choice-actions">
          <button type="button" class="btn btn-primary" data-choice="install">
            Install on device
            <span class="launch-choice-recommended">Recommended</span>
          </button>
          <button type="button" class="btn btn-secondary" data-choice="browser">Run in browser</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const cleanup = (): void => {
      overlay.remove();
    };

    overlay.addEventListener("click", (event) => {
      const target = event.target as HTMLElement | null;
      const button = target?.closest<HTMLButtonElement>("button[data-choice]");
      if (!button) {
        return;
      }
      const choice = button.dataset.choice;
      if (choice === "install") {
        cleanup();
        resolve("install");
        return;
      }
      cleanup();
      resolve("browser");
    });
  });
}

function presentIosInstallHelpDialog(): Promise<void> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "launch-choice-overlay";
    overlay.innerHTML = `
      <div class="launch-choice-card" role="dialog" aria-modal="true" aria-labelledby="install-help-title">
        <h2 id="install-help-title">Install on iPhone or iPad</h2>
        <p>In Safari, tap <strong>Share</strong> and choose <strong>Add to Home Screen</strong> to install Ironfleet CRM.</p>
        <div class="launch-choice-actions">
          <button type="button" class="btn btn-primary" data-action="continue">Continue in browser</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    const continueButton = overlay.querySelector<HTMLButtonElement>("button[data-action='continue']");
    continueButton?.focus();
    continueButton?.addEventListener("click", () => {
      overlay.remove();
      resolve();
    });
  });
}

function presentInstallUnavailableDialog(): Promise<void> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "launch-choice-overlay";
    overlay.innerHTML = `
      <div class="launch-choice-card" role="dialog" aria-modal="true" aria-labelledby="install-unavailable-title">
        <h2 id="install-unavailable-title">App download unavailable</h2>
        <p>Install is not available in this browser right now. Try opening this page in Chrome (Android) or Safari (iPhone/iPad), then use the browser menu to add it to your home screen.</p>
        <div class="launch-choice-actions">
          <button type="button" class="btn btn-primary" data-action="close">OK</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    const closeButton = overlay.querySelector<HTMLButtonElement>("button[data-action='close']");
    closeButton?.focus();
    closeButton?.addEventListener("click", () => {
      overlay.remove();
      resolve();
    });
  });
}

export async function requestAppDownload(): Promise<AppDownloadResult> {
  if (typeof window === "undefined" || isStandalone()) {
    return "unsupported";
  }

  if (deferredInstallPrompt) {
    const promptEvent = deferredInstallPrompt;
    deferredInstallPrompt = null;
    await promptEvent.prompt();
    const decision = await promptEvent.userChoice;
    if (decision.outcome === "accepted") {
      writeLaunchChoiceSeen();
      return "installed";
    }
    writeLaunchChoiceSeen();
    return "dismissed";
  }

  if (isIosBrowser()) {
    await presentIosInstallHelpDialog();
    writeLaunchChoiceSeen();
    return "guided";
  }

  await presentInstallUnavailableDialog();
  return "unsupported";
}

export async function maybeShowLaunchChoice(): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }
  if (isStandalone()) {
    return;
  }
  if (!isAndroidOrIosBrowser()) {
    return;
  }
  if (readLaunchChoiceSeen()) {
    return;
  }

  const choice = await presentLaunchChoiceDialog();
  if (choice === "browser") {
    writeLaunchChoiceSeen();
    return;
  }

  const result = await requestAppDownload();
  if (result === "installed") {
    return;
  }
  writeLaunchChoiceSeen();
}
